import { useCallback, useContext, useState } from 'react';
import useSWR from 'swr';
import DashboardVipGetVipResourceDetail, {
  DashboardVipGetVipResourceDetailRes,
  VipResource,
} from '@quickService/DashboardVipGetVipResourceDetail';
import { UpdateModal } from '@tencent/calendar-web-component-pc-biz';
import { AppContext } from '@utils/browser/app-context';

export interface ResourceDetailData {
  /** 接口数据 */
  vipResource: VipResource | undefined;
  /** 接口错误 */
  error: string | any;
  /** 请求中 */
  isValidating: boolean;
  /** 重新获取新的接口数据 */
  updateVipResource: (hostId?: string) => void;
}

/**
 * 活动商业化-用户版本详细信息获取、更新
 */
export const useResourceDetail = () => {
  const [hostId, setHostId] = useState('');
  const { data, error, mutate, isValidating } = useSWR(
    {
      url: '/v2/activities/dashboard/vip/get-vip-resource-detail',
      params: { hostId },
    },
    async (requestData) => {
      const { hostId: id } = requestData.params;
      if (!id) return Promise.reject('hostId为空');
      const res: DashboardVipGetVipResourceDetailRes = await DashboardVipGetVipResourceDetail({
        host_id: id,
      });
      const { code, msg } = res;
      if (code === 0) return res?.data?.vip_resource;
      return Promise.reject(msg);
    },
    {
      revalidateOnFocus: false,
      refreshInterval: 30 * 60 * 1000,
    },
  );

  const updateVipResource = useCallback(
    (hostId?: string) => {
      if (hostId) setHostId(hostId);
      mutate();
    },
    [mutate, hostId],
  );

  const vipResourceDetail: ResourceDetailData = {
    vipResource: data,
    error,
    isValidating,
    updateVipResource,
  };

  return vipResourceDetail;
};

/**
 * 活动商业化公共逻辑
 */
const useCommercialize = () => {
  const { vipResourceDetail } = useContext(AppContext);
  /**
   * 去升级
   */
  const goUpdate = useCallback(() => {
    window.open('/events/host/service-order');
  }, []);

  /**
   * 弹出升级提示弹窗
   * @param title 标题
   * @param content 内容
   */
  const updateConfirm = (title?: string, content?: string, callBack?: Function) => {
    const closeModal = UpdateModal.show({
      title,
      content,
      onBtnClick() {
        closeModal();
        goUpdate();
        callBack?.();
      },
    });
  };

  return {
    goUpdate, // 去升级
    vipResource: vipResourceDetail?.vipResource, // 接口权限数据
    updateVipResource: vipResourceDetail?.updateVipResource, // 更新权限数据
    vipResourceError: vipResourceDetail?.error, // 权限接口请求错误
    vipResourceLoading: vipResourceDetail?.isValidating, // 权限在请求中
    updateConfirm, // 弹窗提示方法，标题+内容
  };
};

export default useCommercialize;
